/** Heading anchors + table of contents for public help articles. */

export type HelpHeading = {
  /** 1-based source line in the rendered markdown body. */
  readonly line: number;
  /** Markdown level: 1 for `#`, 2 for `##`, … */
  readonly depth: number;
  readonly text: string;
  readonly id: string;
};

export type HelpTocItem = {
  readonly id: string;
  readonly text: string;
  readonly level: 1 | 2;
};

/** Anchor of the child-page list below an article's own text. */
export const HELP_CHILD_LIST_ANCHOR = "articles";

/** Fewer entries than this: no TOC column. */
export const HELP_TOC_MIN_ITEMS = 2;

/** Sticky header (65px) plus breathing room; matches `scroll-mt-[81px]` on headings. */
export const HELP_ANCHOR_OFFSET_PX = 81;

// CommonMark ATX: up to 3 spaces indent; closing #s need whitespace before them.
const ATX_HEADING = /^ {0,3}(#{1,6})[ \t]+(.*?)(?:[ \t]+#+)?[ \t]*$/;
const FENCE = /^\s*(```|~~~)/;

/** German-friendly slug; repeats get -2, -3 … via the shared `used` map. */
export function helpHeadingSlug(
  text: string,
  used: Map<string, number>,
): string {
  const base =
    text
      .toLowerCase()
      .replace(/ä/g, "ae")
      .replace(/ö/g, "oe")
      .replace(/ü/g, "ue")
      .replace(/ß/g, "ss")
      .normalize("NFKD")
      .replace(/\p{Mn}/gu, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "section";
  const seen = used.get(base) ?? 0;
  used.set(base, seen + 1);
  return seen === 0 ? base : `${base}-${seen + 1}`;
}

function plainHeadingText(raw: string): string {
  return raw
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[*_`~]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** ATX headings with stable anchor ids; `reserved` ids (child list) never go to a heading. */
export function extractHelpHeadings(
  markdown: string,
  reserved: readonly string[] = [],
): HelpHeading[] {
  const used = new Map<string, number>(reserved.map((id) => [id, 1]));
  const headings: HelpHeading[] = [];
  let inFence = false;
  markdown.split("\n").forEach((line, index) => {
    if (FENCE.test(line)) {
      inFence = !inFence;
      return;
    }
    if (inFence) return;
    const match = ATX_HEADING.exec(line);
    if (!match) return;
    const text = plainHeadingText(match[2] ?? "");
    if (!text) return;
    headings.push({
      line: index + 1,
      depth: match[1]?.length ?? 1,
      text,
      id: helpHeadingSlug(text, used),
    });
  });
  return headings;
}

/** The two highest heading levels that occur become the TOC; deeper ones stay out. */
export function helpTocItems(headings: readonly HelpHeading[]): HelpTocItem[] {
  const levels = [...new Set(headings.map((heading) => heading.depth))].sort(
    (left, right) => left - right,
  );
  const top = levels[0];
  const second = levels[1];
  if (top === undefined) return [];
  return headings
    .filter((heading) => heading.depth === top || heading.depth === second)
    .map((heading) => ({
      id: heading.id,
      text: heading.text,
      level: heading.depth === top ? 1 : 2,
    }));
}
